/**
 * Shopify Storefront API Client
 * A simple client for making GraphQL requests to Shopify's Storefront API
 */
class ShopifyStorefrontAPI {
    constructor(options = {}) {
      this.endpoint = options.endpoint;
      this.headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
    }
  
    /**
     * Set the access token for API requests
     * @param {string} token - The Storefront API access token
     */
    setAccessToken(token) {
      if (token) {
        this.headers['X-Shopify-Storefront-Access-Token'] = token;
      }
    }
  
    /**
     * Execute a GraphQL query against the Shopify Storefront API
     * @param {string} query - The GraphQL query string
     * @param {Object} variables - Optional variables for the query
     * @returns {Promise<Object>} The query result
     */
    async query(query, variables = {}) {
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({
            query,
            variables
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error executing GraphQL query:', error);
        throw error;
      }
    }
  
    /**
     * Fetch product details by handle
     * @param {string} handle - The product handle
     * @returns {Promise<Object>} The product data
     */
    async getProductByHandle(handle) {
      const query = `
        query getProductByHandle($handle: String!) {
          product(handle: $handle) {
            id
            title
            handle
            description
            images(first: 5) {
              edges {
                node {
                  id
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                }
              }
            }
          }
        }
      `;
      
      return this.query(query, { handle });
    }
  
    /**
     * Fetch collection details by handle
     * @param {string} handle - The collection handle
     * @param {number} productCount - Number of products to fetch
     * @returns {Promise<Object>} The collection data
     */
    async getCollectionByHandle(handle, productCount = 10) {
      const query = `
        query getCollectionByHandle($handle: String!, $productCount: Int!) {
          collection(handle: $handle) {
            id
            title
            handle
            description
            products(first: $productCount) {
              edges {
                node {
                  id
                  title
                  handle
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                      }
                    }
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      `;
      
      return this.query(query, { handle, productCount });
    }

    async getPageByHandle(handle) {
      const query = `
        query getPageByHandle($handle: String!) {
          pageByHandle(handle: $handle) {
            id
            title
            handle
            bodySummary
            body
            createdAt
            updatedAt
            seo {
              title
              description
            }
          }
        }
      `;
      
      return this.query(query, { handle });
    }
    
    /**
     * Fetch product variants with a specific metafield
     * @param {string} productId - The product ID (gid://shopify/Product/XXXXX)
     * @param {string} metafieldKey - The metafield key to query
     * @param {string} metafieldNamespace - The metafield namespace
     * @param {number} variantCount - Number of variants to fetch (max 50)
     * @returns {Promise<Object>} The product variants with specified metafield data
     */
    async getProductVariantsWithMetafield(productId, metafieldKey, metafieldNamespace, variantCount = 50, filterByVariantId = false) {
      const query = `
        query getProductVariantsWithMetafield($productId: ID!, $metafieldKey: String!, $metafieldNamespace: String!, $variantCount: Int!) {
          product(id: $productId) {
            id
            title
            handle
            variants(first: $variantCount) {
              nodes {
                id
                title
                sku
                availableForSale
                metafield(key: $metafieldKey, namespace: $metafieldNamespace) {
                  key
                  namespace
                  value
                }
              }
            }
          }
        }
      `;
      
      return this.query(query, { 
        productId, 
        metafieldKey, 
        metafieldNamespace, 
        variantCount 
      });
    }
  }

window.yr = window.yr || {};
  
window.yr.gql = new ShopifyStorefrontAPI({endpoint: window.yr.settings.storefront_api_url});
window.yr.gql.setAccessToken(window.yr.settings.storefront_access_token);
  